import { useNavigate } from "react-router-dom";
import { formatMass } from "@signolteam/shared/units-of-measure";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { useDCContext } from "@src/context";
import { useOperatorContext } from "@src/context/operatorContext";
import { AppUtils, SignolUtils } from "@src/utils";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import { Graph } from "@src/components/graphs/BehaviourGraphs";
import FeedbackForm from "@src/components/forms/operator/FeedbackForm";
import { StatementBox, Icon, Tooltip } from "@src/components/atoms";
import BehaviourSaveOrFailCard from "@src/components/cards/operator/BehaviourSaveOrFailCard";
import BehaviourInsightCard from "@src/components/cards/operator/BehaviourInsightCard";

const BehaviourResultSection = ({
  behaviour,
  comment,
  customer,
  metadata,
  bColor,
  isOpen,
  event = {},
}) => {
  //past data
  const preferredEventName = customer.preferredEventName;
  const preferredEventNamePlural = customer.preferredEventNamePlural;
  const industry = customer.industry;
  const hasReason = behaviour.reasons.length > 0;

  const checkInsightsStatus = () => {
    if (behaviour?.insightsV2?.length > 0) {
      return true;
    } else if (behaviour?.behaviourAcronym === "ODFL") {
      const df_needed = Number(metadata["discretionary_fuel_needed"]);
      const df_loaded = Number(metadata["discretionary_fuel_loaded"]);
      const df_buffer =
        Number(metadata["constant_df_buffer"]) ||
        Number(metadata["constant_odfl_buffer"]);
      const isSuccess = behaviour?.isSuccess;
      const specialSuccess = isSuccess && df_loaded > df_buffer;
      const useInsight = !isSuccess || specialSuccess;
      return useInsight;
    } else {
      return false;
    }
  };
  const hasInsights = checkInsightsStatus();

  const hasSavings = behaviour.fuelSavedKg && behaviour.CO2SavedKg;
  const showPredictions = customer?.isPredictionsEnabled;

  const { setSelectedEvent } = useOperatorContext();

  const mutationCallback = (feedbackData) => {
    event.comments.push(feedbackData.comment);
    setSelectedEvent(event);
  };

  const mutation = OperatorHooks.useUpdateEventComment(mutationCallback);

  const onSubmit = async (changes) => {
    const feedback = {
      behaviourId: behaviour.id,
      factorIds: [],
      comment: changes,
      eventId: event.id,
      enabled: true,
      invalidText: false,
    };
    trackActionActivity("FEEDBACK_PROVIDED", feedback);
    mutation.mutate(feedback);
  };

  const dc = useDCContext();
  const { getDynamicReplacement, existsDynamicReplacements } = dc;

  const navigate = useNavigate();

  const showODFLTooltip =
    behaviour.behaviourAcronym === "ODFL" &&
    metadata.constant_odfl_buffer &&
    !(behaviour.state === "EXCLUSION") &&
    existsDynamicReplacements(
      `captain__events__behaviour__tooltip__${customer.customerCode}__odfl`.toLowerCase(),
    );

  const ReasonContent = ({
    filterReason,
    acronym,
    name,
    reported,
    selfReportable,
  }) => {
    const reason = behaviour?.reasons?.[0]?.toLowerCase();
    const field_name = reason.split("__").pop();
    const defaultMsg = getDynamicReplacement(
      "captain__events__behaviour__swiper__reason",
      {},
      "",
      true,
    );
    const msgReason = getDynamicReplacement(
      `captain__events__behaviour__reasons__${reason}`,
      {
        VALUE: event[field_name],
        EVENT_NAME: preferredEventNamePlural?.toLowerCase(),
      },
      defaultMsg,
    );
    if (!filterReason && reported) {
      return (
        <StatementBox bgColor={"neutral"}>
          {getDynamicReplacement(
            `captain__events__behaviour__self_report_already_reported`,
            {},
            "",
          )}
        </StatementBox>
      );
    } else if (!!selfReportable && !isOpen) {
      return (
        <StatementBox bgColor={"neutral"} stackText={true}>
          <span>{msgReason}</span>{" "}
          <span>
            {getDynamicReplacement(
              `captain__events__behaviour__self_report_not_current_period`,
              {},
              "",
            )}
          </span>
        </StatementBox>
      );
    }
    return (
      <StatementBox
        bgColor={
          !!filterReason || (!!reason && !selfReportable) ? "neutral" : "red"
        }
      >
        <span>{msgReason}</span>
        {!!selfReportable && !!isOpen && (
          <div
            className="pt-3 cursor-pointer text-sm"
            onClick={() => navigate(`/dashboard/operator/selfreporting`)}
            {...SignolUtils.makeTrackableDataAttributes({
              actionKey: "EVENT_DETAILS_GOTO_SELF_REPORTING",
            })}
          >
            {getDynamicReplacement(
              `captain__self_report__bvr_link${showPredictions ? "_predicted" : ""}`,
              {},
              "",
            )}
          </div>
        )}
        {filterReason && (
          <div
            className="pt-3 cursor-pointer text-sm"
            onClick={() =>
              console.log("TODO - replace with correct navigate action")
            }
            {...SignolUtils.makeTrackableDataAttributes({
              actionKey: "EVENT_DETAILS_GOTO_EXCLUSIONS_FAQ",
            })}
          >
            {getDynamicReplacement(
              `captain__events__behaviour__reasons__not_possible__filtered_settings__faq_link`,
              {},
              "",
            )}
          </div>
        )}
      </StatementBox>
    );
  };

  const stateName = AppUtils.getStateIcon(behaviour.state);

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-row justify-start items-center gap-2 p-4 font-bold">
        <div
          className={`flex justify-center items-center rounded-full bg-${bColor.name}-${behaviour.isSuccess ? "500" : "200"}`}
        >
          <Icon
            key={bColor.name}
            size="10"
            name={stateName}
            inline
            backgroundColor="transparent"
            className="p-1.5"
          />
        </div>
        <div className="flex flex-col md:flex-row justify-center items-start px-4 gap-0 md:gap-4">
          <div className="my-auto font-bold text-lg md:text-xl">
            {behaviour.behaviourName}
          </div>
          {!!behaviour.usingSelfReport && (
            <div className="my-auto md:pt-1 text-md font-normal text-neutral-400">
              {getDynamicReplacement("captain__self_report__bvr_name_text")}
            </div>
          )}
          {!!behaviour.usingPrediction && (
            <div className="my-auto md:pt-1 text-md font-normal text-neutral-400">
              {getDynamicReplacement("captain__prediction__bvr_name_text")}
            </div>
          )}
          {showODFLTooltip && (
            <div className="pt-1">
              <Tooltip
                content={[
                  getDynamicReplacement(
                    `captain__events__behaviour__tooltip__${customer.customerCode}__odfl`.toLowerCase(),
                    {
                      ODFL_BUFFER: formatMass(
                        industry,
                        Number(metadata.constant_odfl_buffer),
                      ),
                    },
                  ),
                ]}
                direction={"right"}
                centered={true}
                absolute={false}
              />
            </div>
          )}
        </div>
      </div>
      {!!hasReason ? (
        <>
          <div className={behaviour.state !== "EXCLUSION" ? "pb-32" : ""}>
            <ReasonContent
              acronym={behaviour.behaviourAcronym}
              name={behaviour.behaviourName}
              reported={!!behaviour.succeededSelfReported}
              selfReportable={behaviour.isSelfReportable}
            />
          </div>
          {behaviour.state === "EXCLUSION" && (
            <FeedbackForm
              existingComment={comment}
              onSubmit={(data) => onSubmit(data)}
              preferredEventName={preferredEventName}
              color="silver"
            />
          )}
        </>
      ) : (
        <>
          <div className="px-4">
            <Graph
              customerCode={customer?.customerCode}
              acronym={`${behaviour.behaviourAcronym}:${behaviour.behaviourInternalTypeAcronym}`}
              metadata={metadata}
              bvrColour={bColor}
              industry={industry}
              featureCheck={AppUtils.isFeatureAvailableForCustomer}
              missingDataMsg={getDynamicReplacement(
                "captain__events__behaviour_graph_missing_data",
              )}
              dc={getDynamicReplacement}
              dcExists={existsDynamicReplacements}
            />
          </div>

          <div className="grow">
            <div className="flex flex-col md:flex-row gap-4 justify-evenly w-full md:w-4/5 m-auto px-2 pb-12">
              {!!hasInsights && (
                <div
                  className={`w-full ${(hasSavings || behaviour.behaviourAcronym === "ODFL") && "sm:w-full md:w-2/3"}`}
                >
                  <BehaviourInsightCard
                    behaviour={behaviour}
                    metadata={metadata}
                    industry={industry}
                    eventType={customer.preferredEventName}
                  />
                </div>
              )}
              {!!hasSavings && (
                <div
                  className={`${!!hasInsights ? "w-full sm:w-full md:w-1/3 flex flex-col justify-start" : "w-full flex flex-row"} `}
                >
                  <BehaviourSaveOrFailCard
                    behaviour={behaviour}
                    industry={industry}
                    msgCO2={getDynamicReplacement(
                      "captain__events__behaviour__swiper__co2_saved_icon",
                      {
                        CO2_SAVED: formatMass(
                          industry,
                          Number(behaviour.CO2SavedKg),
                        ).formattedValue,
                      },
                    )}
                    msgFuel={getDynamicReplacement(
                      "captain__events__behaviour__swiper__fuel_saved_icon",
                      {
                        FUEL_SAVED: formatMass(
                          industry,
                          Number(behaviour.fuelSavedKg.toFixed(0)),
                        ).formattedValue,
                      },
                    )}
                    titleCO2={getDynamicReplacement(
                      "captain__events__behaviour__swiper__title",
                      {},
                    )}
                    titleFuel={getDynamicReplacement(
                      "captain__events__behaviour__swiper__title",
                      {},
                    )}
                    iconCO2={getDynamicReplacement(
                      "captain__events__behaviour__swiper__co2_icon",
                      {},
                    )}
                    iconFuel={getDynamicReplacement(
                      "captain__events__behaviour__swiper__fuel_icon",
                      {},
                    )}
                    hasInsights={hasInsights}
                  />
                </div>
              )}
              {behaviour.behaviourAcronym === "ODFL" && !hasSavings && (
                <div
                  className={`${hasInsights ? "w-full sm:w-1/2 md:w-1/3" : "w-full"}`}
                >
                  <BehaviourSaveOrFailCard
                    industry={industry}
                    msgCO2={`${Number(metadata.discretionary_fuel_needed).toFixed(0)} kg of ${Number(
                      metadata.discretionary_fuel_loaded,
                    ).toFixed(0)} kg.`}
                    msgFuel={`${Number(metadata.discretionary_fuel_loaded_penalty).toFixed(0)} kg of fuel.`}
                    titleCO2={"DF needed"}
                    titleFuel={"Carrying all DF burnt"}
                    hasInsights={hasInsights}
                    behaviour={behaviour}
                  />
                </div>
              )}
            </div>
          </div>
          <FeedbackForm
            existingComment={comment}
            onSubmit={(data) => onSubmit(data)}
            preferredEventName={preferredEventName}
            color={bColor.name}
          />
        </>
      )}
    </div>
  );
};

export const BehaviourResultSectionControls = {};

BehaviourResultSection.propTypes = AppUtils.getPropTypeFromControls(
  BehaviourResultSectionControls,
);

export default BehaviourResultSection;
